import React, { useCallback, useMemo, useState } from "react";
import { COLUMN_DEFINITIONS, PAGE_SIZE_OPTIONS } from "./table.config";
import TableLayout from "../../../layouts/TableLayout/NewTableLayout";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateCustomerModal } from "./CreateModal";
import { useFetchAll } from "../../../hooks/useFetchAll";
import {
  useDeleteCustomerMutation,
  useGetCustomersQuery,
} from "../../../redux/services/customers/customersApi";
import { getCustomerDeleteBlockers } from "./Show/DeleteCustomerCard";
import {
  CustomerStatusFilterList,
  CustomerTagsFilterList,
} from "./CustomerFilters";

const Customers = () => {
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const filterTypes = useMemo(
    () => [
      {
        id: "dateCreated",
        type: "date",
        label: "Created",
        defaultText: "All Time",
        defaultValue: {
          id: "allTime",
          value: null,
        },
      },
      {
        id: "active",
        type: "multiple",
        label: "Status",
        defaultText: "All",
        content: CustomerStatusFilterList,
      },
      {
        id: "tagIds",
        type: "multiple",
        label: "Tags",
        defaultText: "All Tags",
        content: CustomerTagsFilterList,
      },
    ],
    []
  );
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { setFilters },
    queryResult: { data, isLoading, isFetching },
  } = useFetchAll({
    queryHook: useGetCustomersQuery,
    defaultPageSize: 20,
  });
  const [deleteCustomer] = useDeleteCustomerMutation();
  const customers = useMemo(
    () =>
      data?.customers ||
      (Array.isArray(data?.data) && data.data) ||
      data?.customer ||
      [],
    [data]
  );

  const handleEdit = useCallback(
    (itemId) => {
      navigate(`/admin/customers/${itemId}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (items) => {
      try {
        const blockers = getCustomerDeleteBlockers(items[0]);
        if (blockers.length > 0) {
          toast(
            `Resolve ${blockers.length} customer blocker${
              blockers.length === 1 ? "" : "s"
            } before deleting this customer.`,
            {
              type: "error",
              toastId: "customerDeleteBlocked",
            }
          );
          return {
            status: "error",
            message: "Customer deletion blocked",
          };
        }

        const res = await deleteCustomer({ id: items[0]._id }).unwrap();
        toast(res.message, {
          type: res.status,
          toastId: "customerDeleted",
        });

        return res;
      } catch (error) {
        toast(error?.data?.message || "Unable to delete customer", {
          type: "error",
          toastId: "customerDeleteError",
        });
        throw error;
      }
    },
    [deleteCustomer]
  );

  return (
    <div className="w-full h-full min-h-0">
      <TableLayout
        id="customers"
        title="Customers"
        buttonSize="xs"
        loading={isLoading || isFetching}
        columns={COLUMN_DEFINITIONS}
        data={customers}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        pagination={data}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        mainAction={{
          title: "Create Customer",
          onClick: () => setModalVisible(true),
        }}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        globalSearchFilters={["name", "phone", "email", "slug"]}
        fullTextSearch={handleGlobalSearch}
        filters={filterTypes}
        onFiltersChange={setFilters}
      />
      <CreateCustomerModal
        visible={modalVisible}
        setVisible={setModalVisible}
      />
    </div>
  );
};

export default Customers;
